import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { QueryState } from '@/components/QueryState'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { AccountFormDialog } from '@/features/accounting/AccountFormDialog'
import {
  ACCOUNT_TYPE_LABELS,
  NORMAL_BALANCE,
  deleteAccount,
  listAccounts,
  listPostingRules,
  toTree,
  type Account,
  type AccountNode,
  type PostingRule,
} from '@/features/accounting/api'
import { useAuth } from '@/features/auth/store'
import { toast } from '@/lib/toast'

const TYPE_TONE: Record<string, string> = {
  asset: 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200',
  liability: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  equity: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  revenue: 'bg-success/10 text-success',
  expense: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
}

function TypeBadge({ type }: { type: AccountNode['type'] }) {
  return (
    <span
      className={`rounded px-2 py-0.5 text-xs font-medium ${TYPE_TONE[type] ?? TYPE_TONE.expense}`}
    >
      {ACCOUNT_TYPE_LABELS[type]}
    </span>
  )
}

/**
 * Depth, carried by weight rather than by a left margin. Padding a cell moves its text off
 * the column it is supposed to be in, and a staircase down the middle of a table is harder
 * to read than the thing it was meant to clarify -- codes are the tree anyway: 1110 sits
 * under 1100 sits under 1000, which is what the numbering is for.
 */
const DEPTH_WEIGHT = ['font-semibold', 'font-medium', 'font-normal'] as const

/**
 * The chart, as a flat table read down the code column rather than a collapsible tree.
 *
 * Thirty-odd accounts three levels deep fit on one screen, and a finance user reading a
 * chart wants to see the whole shape of it at once. Collapsing is what you add when there
 * are hundreds; until then it only hides the thing the screen exists to show.
 */
const columns = (
  canEdit: boolean,
  canDelete: boolean,
  onEdit: (account: Account) => void,
  onDelete: (account: Account) => void,
): Column<AccountNode>[] => [
  {
    key: 'code',
    header: 'Code',
    className: 'font-mono text-xs',
    cell: (account) => account.code,
  },
  {
    key: 'name',
    header: 'Account',
    cell: (account) => (
      <span className={DEPTH_WEIGHT[Math.min(account.depth, 2)]}>
        {canEdit ? (
          <button
            type="button"
            onClick={() => onEdit(account)}
            className="text-left hover:underline"
          >
            {account.name}
          </button>
        ) : (
          account.name
        )}
      </span>
    ),
  },
  {
    key: 'type',
    header: 'Type',
    cell: (account) => <TypeBadge type={account.type} />,
  },
  {
    key: 'normal_balance',
    header: 'Increases on',
    className: 'text-slate-500 dark:text-slate-400',
    cell: (account) => NORMAL_BALANCE[account.type],
  },
  {
    key: 'is_postable',
    header: 'Posting',
    cell: (account) =>
      account.is_postable ? 'Postable' : <span className="text-slate-400">Heading only</span>,
  },
  {
    key: 'is_active',
    header: 'Status',
    cell: (account) =>
      account.is_active ? (
        <span className="rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
          Active
        </span>
      ) : (
        <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          Archived
        </span>
      ),
  },
  {
    key: 'actions',
    header: '',
    headerClassName: 'text-right',
    className: 'text-right whitespace-nowrap',
    cell: (account) => (
      <span className="inline-flex items-center gap-1">
        {account.is_postable && (
          <Link
            to={`/accounting/ledger/${account.id}`}
            className="rounded px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-900/40"
          >
            Ledger
          </Link>
        )}
        {canDelete && (
          <Button variant="ghost" size="sm" onClick={() => onDelete(account)}>
            Delete
          </Button>
        )}
      </span>
    ),
  },
]

/**
 * How an event becomes an entry, shown but not edited here.
 *
 * The rules are what makes an invoice post itself, so finance needs to be able to read them
 * without opening the database. Editing them is a route the API exposes and a screen this
 * one does not have yet -- a wrong rule silently misposts every future invoice, and that
 * deserves a form with more warning around it than a table of dropdowns.
 */
function PostingRules({ accounts }: { accounts: Account[] }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['posting-rules'],
    queryFn: listPostingRules,
    retry: false,
  })

  const nameOf = (id: string) => {
    const account = accounts.find((candidate) => candidate.id === id)
    return account ? `${account.code} ${account.name}` : '—'
  }

  const rules = data?.data ?? []

  return (
    <Card>
      <CardHeader
        title="Posting rules"
        description="Every automatic entry comes from one of these. Both legs take the same amount, so a rule cannot post an unbalanced entry."
      />
      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={rules.length === 0}
        emptyMessage="No posting rules yet. Run the chart-of-accounts seed."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-2 font-medium">Event</th>
                <th className="px-5 py-2 font-medium">Rule</th>
                <th className="px-5 py-2 font-medium">Debit</th>
                <th className="px-5 py-2 font-medium">Credit</th>
                <th className="px-5 py-2 font-medium">Amount from</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rules.map((rule: PostingRule) => (
                <tr key={rule.id} className={rule.is_active ? undefined : 'opacity-50'}>
                  <td className="px-5 py-2 font-mono text-xs">{rule.event_type}</td>
                  <td className="px-5 py-2">
                    {rule.name}
                    {rule.match_key && (
                      <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        {rule.match_key}={rule.match_value}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2">{nameOf(rule.debit_account_id)}</td>
                  <td className="px-5 py-2">{nameOf(rule.credit_account_id)}</td>
                  <td className="px-5 py-2 font-mono text-xs">{rule.amount_field}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </QueryState>
    </Card>
  )
}

export function ChartOfAccounts() {
  const [dialog, setDialog] = useState<{ account?: Account } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Account | null>(null)
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => listAccounts(),
    retry: false,
  })

  const accounts = useMemo(() => data?.data ?? [], [data])
  const rows = useMemo(() => toTree(accounts), [accounts])

  const remove = useMutation({
    mutationFn: (account: Account) => deleteAccount(account.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] })
      toast.success('Account deleted.')
      setConfirmDelete(null)
    },
    onError: (mutationError: unknown) => {
      // 409 is the interesting one: the account has been posted to, so it can only be
      // archived. Saying that is more useful than the generic failure.
      toast.error(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(null)
    },
  })

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Chart of accounts</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {isLoading
              ? 'Loading…'
              : `${accounts.length} account${accounts.length === 1 ? '' : 's'}`}
          </p>
        </div>
        {can('chart_of_accounts', 'create') && (
          <Button onClick={() => setDialog({})}>New account</Button>
        )}
      </header>

      <Card>
        <CardHeader
          title="Accounts"
          description="In code order, parents before their children. A heading carries no postings of its own; it totals what sits beneath it."
        />
        <DataTable
          rows={rows}
          columns={columns(
            can('chart_of_accounts', 'update'),
            can('chart_of_accounts', 'delete'),
            (account) => setDialog({ account }),
            (account) => setConfirmDelete(account),
          )}
          getRowId={(account) => account.id}
          caption="Chart of accounts"
          isLoading={isLoading}
          error={error}
          emptyMessage="No accounts yet. Run the chart-of-accounts seed."
          search={{
            placeholder: 'Search code or name…',
            matches: (account, query) =>
              account.code.toLowerCase().includes(query) ||
              account.name.toLowerCase().includes(query),
          }}
          // Big enough that a seeded chart stays on one page: the rows are a flattened
          // tree, and a page break puts indented children above the parent they hang off.
          pageSize={50}
        />
      </Card>

      <PostingRules accounts={accounts} />

      {dialog && (
        <AccountFormDialog
          open
          account={dialog.account}
          accounts={accounts}
          onClose={() => setDialog(null)}
        />
      )}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.code}?` : 'Delete account'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Alert tone="info">
          Only an account nothing has ever been posted to can be deleted. If it has history, uncheck{' '}
          <span className="font-medium">Active</span> instead — that hides it from pickers and
          leaves the books intact.
        </Alert>
      </Modal>
    </div>
  )
}
